import { CheckboxInput } from '@frontend/components/basic-forms';
import { ReviewOutcome } from '@frontend/review/types';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import useVendingReviewResolve from './vending-review-resolve.controller';

const ID = 'vending-review-resolve';

const VendingReviewResolve = () => {
    const viewProps = useVendingReviewResolve();

    return (
        <WizardScreenContainer
            id={ID}
            loadingModal={{
                show: viewProps.isProcessing,
                onClose: viewProps.stopProcessing
            }}
            footer={{
                onSubmit: viewProps.handleSubmit,
                submitDisabled: viewProps.isProcessing || !viewProps.selectedOption
            }}>
            <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                <div className='text-foreground-default justify-end'>
                    <FormattedMessage
                        defaultMessage='Selected product'
                        id='kiosk.wizard.lending-review-detail.selected-product'
                        description='Instruction text on the lending review detail screen, showing the selected product'
                    />
                </div>
                <ProductCard
                    product={viewProps.product}
                    variant='attributes'
                />

                <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                    <div className='text-foreground-default justify-end'>
                        <FormattedMessage
                            defaultMessage='My review'
                            id='kiosk.wizard.lending-review-detail.my-review'
                            description='Label for the section where the user can provide their review'
                        />
                    </div>
                    <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                        <RadioButton
                            onClick={() => viewProps.setSelectedOption(ReviewOutcome.OK)}
                            selected={viewProps.selectedOption === ReviewOutcome.OK}
                            decline
                            text={
                                <FormattedMessage
                                    id='kiosk.wizard.review-resolve.ok'
                                    defaultMessage='Everything is OK'
                                />
                            }
                        />
                        <RadioButton
                            onClick={() => viewProps.setSelectedOption(ReviewOutcome.WRONG)}
                            selected={viewProps.selectedOption === ReviewOutcome.WRONG}
                            decline
                            text={
                                <FormattedMessage
                                    id='kiosk.wizard.review-resolve.wrong-product'
                                    defaultMessage='Wrong product'
                                />
                            }
                        />
                        <RadioButton
                            onClick={() => viewProps.setSelectedOption(ReviewOutcome.DAMAGED)}
                            selected={viewProps.selectedOption === ReviewOutcome.DAMAGED}
                            decline
                            text={
                                <FormattedMessage
                                    id='kiosk.wizard.lending-review-detail.damaged-product'
                                    defaultMessage='Damaged product'
                                />
                            }
                        />
                        <RadioButton
                            onClick={() => viewProps.setSelectedOption(ReviewOutcome.MISSING)}
                            selected={viewProps.selectedOption === ReviewOutcome.MISSING}
                            decline
                            text={
                                <FormattedMessage
                                    id='kiosk.wizard.review-resolve.slot-empty'
                                    defaultMessage='SLOT empty'
                                />
                            }
                        />
                    </div>
                </div>

                {viewProps.selectedOption === ReviewOutcome.OK && (
                    <button
                        className='text-foreground-default disabled:text-brand-disabled bg-background-surface disabled:bg-background-default border-border-default inline-flex items-center justify-start gap-4 self-stretch rounded-xl border p-4'
                        onClick={(e) => {
                            e.stopPropagation();
                            viewProps.toggleAddToStock();
                        }}>
                        <CheckboxInput value={viewProps.addToStock} />
                        <div className='inline-flex flex-1 flex-col items-start justify-center gap-0.5'>
                            <div className='justify-start'>
                                <FormattedMessage
                                    defaultMessage='Add this product to stock'
                                    id='kiosk.wizard.vending-review-detail.add-to-stock'
                                    description='Label for the checkbox to add the selected product to stock'
                                />
                            </div>
                        </div>
                    </button>
                )}
            </div>
        </WizardScreenContainer>
    );
};

export default VendingReviewResolve;

const RadioButton = ({ decline, text, onClick, selected }: { decline?: boolean; text: React.ReactNode; onClick: () => void; selected: boolean }) => {
    return (
        <button
            onClick={onClick}
            data-variant='Detailed'
            className='bg-background-surface border-border-default inline-flex items-center justify-start gap-4 self-stretch rounded-xl border px-4 py-3'>
            <div
                data-variant='Checked'
                className='bg-background-surface border-border-default flex h-4 w-4 items-center justify-center rounded-full border'>
                <div className={`size-2.5 rounded-full ${selected ? 'bg-brand-primary' : ''}`} />
            </div>

            <div className='inline-flex flex-1 flex-col items-start justify-center gap-0.5'>
                <div className='text-foreground-default justify-start text-base font-semibold'>{text}</div>
            </div>
        </button>
    );
};
